import {
  useMemo,
  useState,
  useContext,
  createContext,
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useChatbotSession } from 'queries/chatbot/queries';
import { Message } from '../Hooks/useLangflowChat';
import { useQueryClient } from '@tanstack/react-query';
import { ChatMutationResponse } from 'queries/chatbot/contract';

type Chat = {
  sessionId: string;
  messages: Message[];
  lastMessageDate: Dayjs;
};

export type LangflowChatContextType = {
  chats: Chat[];
  appendMessageToChat: ({ sessionId, message }: { sessionId: string; message: Message }) => void;
  getChatMessages: (sessionId: string) => Message[];
  selectedSessionId: string;
  setSelectedSessionId: Dispatch<SetStateAction<string>>;
  updateChatSessionId: ({
    oldSessionId,
    newSessionId,
  }: {
    oldSessionId: string;
    newSessionId: string;
  }) => void;
  deleteChat: (sessionId: string) => void;
  isHistorySession: boolean;
  setIsHistorySession: Dispatch<SetStateAction<boolean>>;
};

export const LangflowChatContext = createContext<LangflowChatContextType>({
  chats: [],
  appendMessageToChat: () => undefined,
  getChatMessages: () => [],
  selectedSessionId: '',
  setSelectedSessionId: () => '',
  updateChatSessionId: () => undefined,
  deleteChat: () => undefined,
  isHistorySession: false,
  setIsHistorySession: () => undefined,
});

const getInitialChats = (): Chat[] => {
  const storedSessions = localStorage.getItem('LANGFLOW_SESSIONS');
  if (storedSessions) {
    return JSON.parse(storedSessions).map(
      (session: { sessionId: string; lastMessageDate: string }) => ({
        sessionId: session.sessionId,
        messages: [],
        lastMessageDate: dayjs(session.lastMessageDate),
      }),
    );
  }
  return [];
};

const LangflowChatContextProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const lastChatQuery = queryClient.getMutationCache().find({
    mutationKey: ['LANGFLOW_PROMPT'],
  })?.state?.data as ChatMutationResponse;
  const session_id = lastChatQuery?.sessionId;
  const [chats, setChats] = useState<Chat[]>(getInitialChats());
  const [selectedSessionId, setSelectedSessionId] = useState<string>(session_id);
  const [isHistorySession, setIsHistorySession] = useState(false);

  useEffect(() => {
    const previews = chats.map(({ sessionId, lastMessageDate }) => ({
      sessionId,
      lastMessageDate,
    }));

    localStorage.setItem('LANGFLOW_SESSIONS', JSON.stringify(previews));
  }, [chats]);

  const { data } = useChatbotSession(selectedSessionId, {
    enabled: !!selectedSessionId && isHistorySession,
  });

  useEffect(() => {
    if (!selectedSessionId || !data) return;

    setChats((oldChats) => {
      const newChats = [...oldChats];
      const foundChat = newChats.find((chat) => chat.sessionId === selectedSessionId);
      if (foundChat)
        foundChat.messages = data.map(
          (item) =>
            ({
              id: item.id,
              content: item.text,
              sender: item.sender,
              date: item.timestamp,
            } as Message),
        );

      return newChats;
    });
  }, [selectedSessionId, data]);

  const updateChatSessionId = useCallback(
    ({ oldSessionId, newSessionId }: { oldSessionId: string; newSessionId: string }) => {
      setChats((oldChats) => {
        const chatIndex = oldChats.findIndex((chat) => chat.sessionId === oldSessionId);
        if (chatIndex === -1) return oldChats;

        const updatedChats = [...oldChats];
        updatedChats[chatIndex] = {
          ...updatedChats[chatIndex],
          sessionId: newSessionId,
        };

        return updatedChats;
      });
    },
    [setChats],
  );

  const appendMessageToChat = useCallback(
    ({ sessionId, message }: { sessionId: string; message: Message }) => {
      setChats((oldChats) => {
        const existingChatIndex = oldChats.findIndex((chat) => chat.sessionId === sessionId);
        const now = dayjs();

        if (existingChatIndex !== -1) {
          const updatedChats = [...oldChats];
          updatedChats[existingChatIndex] = {
            ...updatedChats[existingChatIndex],
            messages: [...updatedChats[existingChatIndex].messages, message],
            lastMessageDate: now,
          };
          return updatedChats;
        } else {
          return [
            ...oldChats,
            {
              sessionId,
              messages: [message],
              lastMessageDate: now,
            },
          ];
        }
      });
    },
    [],
  );

  const deleteChat = useCallback(
    (sessionId: string) => {
      setChats((oldChats) => {
        return oldChats.filter((chat) => chat.sessionId !== sessionId);
      });
    },
    [setChats],
  );

  const getChatMessages = useCallback(() => {
    return chats.find((chat) => chat.sessionId === selectedSessionId)?.messages ?? [];
  }, [chats, selectedSessionId]);

  const context = useMemo(
    () => ({
      chats,
      appendMessageToChat,
      getChatMessages,
      selectedSessionId,
      setSelectedSessionId,
      updateChatSessionId,
      deleteChat,
      isHistorySession,
      setIsHistorySession,
    }),
    [
      chats,
      appendMessageToChat,
      getChatMessages,
      selectedSessionId,
      updateChatSessionId,
      setSelectedSessionId,
      deleteChat,
      isHistorySession,
      setIsHistorySession,
    ],
  );

  return <LangflowChatContext.Provider value={context}>{children}</LangflowChatContext.Provider>;
};

export default LangflowChatContextProvider;

export const useLangflowChatContext = () => {
  return useContext(LangflowChatContext);
};
